import { Box, Chip, Skeleton, Typography } from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  Camera,
  DirectionsCar,
  EscalatorWarning,
  Face,
  Mood,
  PhotoCamera,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { EventDiscriminator, IEventSimpleProps } from '../../../services/Events/types';
import { useGetFaceRecognitionEventByIdAsync } from '../../../services/Events/FaceRecognitionEventService/hooks/useMutation';
import { useGetIvsEventByIdAsync } from '../../../services/Events/IvsEventsService/hooks/useMutation';
import { useGetStudentRecognitionEventByIdAsync } from '../../../services/Events/StudentRecognitionEventsService/hooks/useMutation';
import { useGetResponsibleRecognitionEventByIdAsync } from '../../../services/Events/ResponsibleRecognitionEventsService/hooks/useMutation';
import { useGetLprEventByIdAsync } from '../../../services/Events/LprEventsService/hooks/useMutation';
import { useFirebaseImageMutateFromUrl } from '../../../firebase';

// ─── Design tokens (must match MonitoringPage) ────────────────────────────────
const C = {
  card:   '#111d32',
  panel:  '#0e1728',
  head:   '#0d1a2f',
  border: 'rgba(255,255,255,0.07)',
  text:   '#e2e8f0',
  sub:    '#7a8699',
  accent: '#3b82f6',
};

const noScrollbar = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
};

// ─── Type helpers ─────────────────────────────────────────────────────────────
const getTypeInfo = (discriminator: EventDiscriminator | string) => {
  switch (discriminator) {
    case 'FaceRecognitionEventEntity':
      return { icon: <Mood sx={{ fontSize: 16 }} />,            color: '#34d399', bg: 'rgba(52,211,153,0.12)',   label: 'Facial'       };
    case 'LprEventEntity':
      return { icon: <DirectionsCar sx={{ fontSize: 16 }} />,   color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',   label: 'LPR'          };
    case 'IvsEventEntity':
      return { icon: <Camera sx={{ fontSize: 16 }} />,          color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',   label: 'IVS'          };
    case 'StudentRecognitionEventEntity':
      return { icon: <Face sx={{ fontSize: 16 }} />,            color: '#f472b6', bg: 'rgba(244,114,182,0.12)', label: 'Aluno'        };
    case 'ResponsibleRecognitionEventEntity':
      return { icon: <EscalatorWarning sx={{ fontSize: 16 }} />, color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', label: 'Responsável'  };
    default:
      return { icon: <Camera sx={{ fontSize: 16 }} />,          color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', label: 'Evento'       };
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const DetailRow = ({
  label,
  value,
  accent,
}: {
  label: string;
  value?: string | number;
  accent?: boolean;
}) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    py: 0.85,
    borderBottom: `1px solid ${C.border}`,
    '&:last-child': { borderBottom: 'none' },
  }}>
    <Typography fontSize={12} color={C.sub} sx={{ flexShrink: 0, mr: 1 }}>
      {label}
    </Typography>
    <Typography
      fontSize={12}
      color={accent ? C.accent : C.text}
      fontWeight={accent ? 600 : 400}
      sx={{ textAlign: 'right', wordBreak: 'break-word' }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

// ─── Props ────────────────────────────────────────────────────────────────────
interface LastEventProps {
  title: string;
  event: IEventSimpleProps | undefined;
  onClickInImage?: (imageUrl: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
const LastEvent = ({ event, onClickInImage }: LastEventProps) => {
  const [imageToRender, setImageToRender] = useState<string | undefined>();
  const [eventDetails, setEventDetails]   = useState<any>(null);

  const { mutateAsync: getFace }        = useGetFaceRecognitionEventByIdAsync();
  const { mutateAsync: getLpr }         = useGetLprEventByIdAsync();
  const { mutateAsync: getIvs }         = useGetIvsEventByIdAsync();
  const { mutateAsync: getStudent }     = useGetStudentRecognitionEventByIdAsync();
  const { mutateAsync: getResponsible } = useGetResponsibleRecognitionEventByIdAsync();
  const { mutate: loadImage, isLoading: isLoadingImage } = useFirebaseImageMutateFromUrl();

  // Load extended event details
  useEffect(() => {
    setEventDetails(null);
    if (!event?.id) return;
    (async () => {
      try {
        switch (event.discriminator) {
          case 'FaceRecognitionEventEntity':    setEventDetails(await getFace(event.id));        break;
          case 'LprEventEntity':                setEventDetails(await getLpr(event.id));         break;
          case 'IvsEventEntity':                setEventDetails(await getIvs(event.id));         break;
          case 'StudentRecognitionEventEntity': setEventDetails(await getStudent(event.id));     break;
          case 'ResponsibleRecognitionEventEntity': setEventDetails(await getResponsible(event.id)); break;
        }
      } catch { /* ignore */ }
    })();
  }, [event?.id]);

  // Load image via Firebase
  useEffect(() => {
    setImageToRender(undefined);
    if (!event) return;

    const publicUrl =
      event.carImage?.includes('https')         ? event.carImage         :
      event.recognitionImage?.includes('https') ? event.recognitionImage :
      event.image?.includes('https')            ? event.image            : '';

    if (!publicUrl) return;

    loadImage(publicUrl, {
      onSuccess: (url) => setImageToRender(url ?? undefined),
      onError:   ()    => setImageToRender(undefined),
    });
  }, [event]);

  // ── Empty state ──
  if (!event) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 1.5, px: 3,
      }}>
        <PhotoCamera sx={{ fontSize: 44, color: C.sub, opacity: 0.35 }} />
        <Typography fontSize={13} color={C.sub} textAlign="center">
          Selecione um evento na tabela para ver os detalhes
        </Typography>
      </Box>
    );
  }

  const typeInfo   = getTypeInfo(event.discriminator);
  const personName = eventDetails?.clientName || eventDetails?.plate;
  const dateObj    = event.createdAt ? new Date(event.createdAt) : null;
  const dateStr    = dateObj?.toLocaleDateString('pt-BR') ?? '—';
  const timeStr    = dateObj?.toLocaleTimeString('pt-BR') ?? '—';

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%',
      overflowY: 'auto',
      ...noScrollbar,
    }}>
      {/* ── Person / type header ── */}
      <Box sx={{
        px: 2.5, py: 1.75,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: `1px solid ${C.border}`,
        flexShrink: 0,
      }}>
        {/* Type icon circle */}
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%',
          background: typeInfo.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: typeInfo.color, flexShrink: 0,
          border: `1.5px solid ${typeInfo.color}33`,
        }}>
          {typeInfo.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={13} fontWeight={700} color={C.text} noWrap>
            {personName || event.device?.name || 'Evento detectado'}
          </Typography>
          {eventDetails?.confidence !== undefined && (
            <Typography fontSize={11} color={C.sub}>
              Confiança:{' '}
              <Box component="span" sx={{ color: '#34d399', fontWeight: 700 }}>
                {(eventDetails.confidence * 100).toFixed(1)}%
              </Box>
            </Typography>
          )}
        </Box>

        <Chip
          label={typeInfo.label}
          size="small"
          sx={{
            color: typeInfo.color, background: typeInfo.bg,
            fontWeight: 700, fontSize: 11, height: 22,
            '& .MuiChip-label': { px: 1 },
            flexShrink: 0,
          }}
        />
      </Box>

      {/* ── Image ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{
          borderRadius: 2, overflow: 'hidden',
          border: `1px solid ${C.border}`,
          background: C.panel,
        }}>
          {isLoadingImage ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={200}
              sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
            />
          ) : imageToRender ? (
            <Box
              component="img"
              src={imageToRender}
              alt="event"
              onClick={() => onClickInImage && onClickInImage(
                event.recognitionImage || event.carImage || event.image || ''
              )}
              sx={{
                width: '100%', height: 280,
                objectFit: 'cover', display: 'block',
                cursor: onClickInImage ? 'pointer' : 'default',
                transition: 'opacity 0.15s',
                '&:hover': { opacity: onClickInImage ? 0.85 : 1 },
              }}
            />
          ) : (
            <Box sx={{
              height: 200,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
              <PhotoCamera sx={{ fontSize: 36, color: C.sub, opacity: 0.5 }} />
              <Typography fontSize={12} color={C.sub}>Sem imagem</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Details grid ── */}
      <Box sx={{
        mx: 2.5, mb: 2.5,
        background: C.panel,
        borderRadius: 1.5,
        px: 1.5, py: 0.5,
        border: `1px solid ${C.border}`,
        flexShrink: 0,
      }}>
        <DetailRow label="Dispositivo"           value={event.device?.name}  />
        <DetailRow label="Data"                  value={dateStr}             />
        <DetailRow label="Horário"               value={timeStr}             />
        <DetailRow label="Tipo de reconhecimento" value={typeInfo.label}     accent />
        {event.description && (
          <DetailRow label="Descrição"           value={event.description}   />
        )}
        {eventDetails?.plate && !personName && (
          <DetailRow label="Placa"               value={eventDetails.plate}  accent />
        )}
        {personName && personName !== event.device?.name && (
          <DetailRow label="Identificado"        value={personName}          />
        )}
      </Box>
    </Box>
  );
};

export default LastEvent;
